import * as React from 'react'
import {
  useDrag,
  useDrop,
  DropTargetMonitor,
  DragSourceMonitor,
} from 'react-dnd'
import { XYCoord } from 'dnd-core'
import ItemTypes from './ItemTypes'
import { Creative, CreativeAction } from '../../../interfaces/entities/creative'
import CreativeCard from '../CreativeCard'
import { SelectableCreativeCard } from '../../../interfaces/entities/cards'

const style = {
  margin: '15px 15px',

  card: {},
}

interface CardProps {
  id: any
  creativeCard: SelectableCreativeCard
  index: number
  formatSetType?: string
  doCreativeAction?: (creative: Creative, action: CreativeAction) => any
  moveCard: (cardId: string, atIndex: number, save?: boolean) => void
}

interface DragItem {
  id: any
  index: number
}

const Card = (props: CardProps) => {
  const { creativeCard, doCreativeAction, formatSetType, index } = props
  const ref = React.useRef<HTMLDivElement>(null)

  const [, drop] = useDrop<DragItem>({
    accept: ItemTypes.CARD,
    hover(item: DragItem, monitor: DropTargetMonitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current.getBoundingClientRect()

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      // Get horizontal middle
      const hoverMiddleX =
        (hoverBoundingRect.right - hoverBoundingRect.left) / 2

      // Determine mouse position
      const clientOffset = monitor.getClientOffset()

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

      // Get pixels to the left
      const hoverClientX = (clientOffset as XYCoord).x - hoverBoundingRect.left

      const upwards = dragIndex > hoverIndex && hoverClientY > hoverMiddleY
      const downwards = dragIndex < hoverIndex && hoverClientY < hoverMiddleY
      const leftwards = dragIndex > hoverIndex && hoverClientX > hoverMiddleX
      const rightwards = dragIndex < hoverIndex && hoverClientX < hoverMiddleX

      if (upwards && (leftwards || rightwards)) {
        return
      }

      if (downwards && (leftwards || rightwards)) {
        return
      }

      // Time to actually perform the action
      props.moveCard(item.id, hoverIndex)

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CARD,
    item: { id: props.id, index: props.index },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item: DragItem) => {
      props.moveCard(item.id, item.index, true)
    },
  })

  drag(drop(ref))

  return (
    <div ref={ref} style={{ ...style }}>
      <CreativeCard
        navigateOnClick={true}
        state={
          isDragging
            ? 'dragging'
            : creativeCard.status !== undefined
              ? creativeCard.status
              : null
        }
        creative={creativeCard.card.creative}
        doCreativeAction={doCreativeAction}
        formatSetType={formatSetType}
      />
    </div>
  )
}

export default Card
